/**
 * Drag bounds for every editable handle, and the minimum sizes they imply.
 *
 * The editor calls the *Range / *Max functions once at the start of a drag and
 * clamps the pointer within them, so a drag pegs exactly at the edge of
 * validity and the user can never produce overlapping or inverted geometry.
 */
import { alongLeg, edgeLeg, isHorizontal, perpLeg } from '../geometry';
import { type CornerName, type EdgeName, type FloorPlanEdgeSegment, type FloorPlanRoom, SQUARE_CORNER } from '../schemas';
import { alcoveNeighbors, CORNER_LEG_EDGE, edgeLength, lastSegmentEnd, segmentsInPositionOrder } from './edgeTopology';

export interface Range {
  min: number;
  max: number;
}

/** Smallest valid alcove width so the back-corner tip chamfers don't cross. */
export function minAlcoveWidth(alcove: FloorPlanEdgeSegment, edge: EdgeName): number {
  const horizontal = isHorizontal(edge);
  return alongLeg(alcove.backLeftCorner ?? SQUARE_CORNER, horizontal) + alongLeg(alcove.backRightCorner ?? SQUARE_CORNER, horizontal);
}

/** Smallest valid alcove depth so a back-corner chamfer doesn't invert a side wall. */
export function minAlcoveDepth(alcove: FloorPlanEdgeSegment, edge: EdgeName): number {
  const horizontal = isHorizontal(edge);
  return Math.max(perpLeg(alcove.backLeftCorner ?? SQUARE_CORNER, horizontal), perpLeg(alcove.backRightCorner ?? SQUARE_CORNER, horizontal));
}

/** Max along-edge inset a corner chamfer may take without hitting an alcove or the opposite corner. */
export function maxCornerInset(
  shape: FloorPlanRoom,
  edge: EdgeName,
  role: 'start' | 'end',
): number {
  const length = edgeLength(shape, edge);
  if (role === 'start') {
    const sorted = segmentsInPositionOrder(shape, edge);
    const firstPosition = sorted.length ? sorted[0].segment.position : length;
    return Math.max(0, Math.min(firstPosition, length - edgeLeg(shape, edge, false)));
  }
  return Math.max(0, Math.min(length - lastSegmentEnd(shape, edge), length - edgeLeg(shape, edge, true)));
}

/** Max {dx, dy} legs for a corner chamfer, given everything else fixed. */
export function cornerChamferMax(
  shape: FloorPlanRoom,
  corner: CornerName,
): { dx: number; dy: number } {
  const legEdges = CORNER_LEG_EDGE[corner];
  return {
    dx: maxCornerInset(shape, legEdges.dx[0], legEdges.dx[1]),
    dy: maxCornerInset(shape, legEdges.dy[0], legEdges.dy[1]),
  };
}

/** Valid range for an alcove's position (slides between its neighbors). */
export function alcovePositionRange(
  shape: FloorPlanRoom,
  edge: EdgeName,
  storageIndex: number,
): Range {
  const { segment, previousEnd, nextStart } = alcoveNeighbors(shape, edge, storageIndex);
  return { min: previousEnd, max: Math.max(previousEnd, nextStart - segment.width) };
}

/** Valid range for an alcove's width (grows toward the next neighbor). */
export function alcoveWidthRange(
  shape: FloorPlanRoom,
  edge: EdgeName,
  storageIndex: number,
): Range {
  const { segment, nextStart } = alcoveNeighbors(shape, edge, storageIndex);
  const min = minAlcoveWidth(segment, edge);
  return { min, max: Math.max(min, nextStart - segment.position) };
}

/** Valid range for an alcove's depth (outward, so no upper bound). */
export function alcoveDepthRange(
  shape: FloorPlanRoom,
  edge: EdgeName,
  storageIndex: number,
): Range {
  const { segment } = alcoveNeighbors(shape, edge, storageIndex);
  return { min: minAlcoveDepth(segment, edge), max: Infinity };
}

/**
 * Valid position range when dragging the LEFT wall of an alcove (keeps right wall fixed).
 * The right wall stays at position + width; only position changes.
 */
export function alcoveLeftWallRange(shape: FloorPlanRoom, edge: EdgeName, storageIndex: number): Range {
  const { segment, previousEnd } = alcoveNeighbors(shape, edge, storageIndex);
  const rightWall = segment.position + segment.width;
  return { min: previousEnd, max: Math.max(previousEnd, rightWall - minAlcoveWidth(segment, edge)) };
}

/**
 * Valid end-position range when dragging the RIGHT wall of an alcove (keeps left wall fixed).
 * The left wall stays at position; only width changes, via newEnd = position + width.
 */
export function alcoveRightWallRange(shape: FloorPlanRoom, edge: EdgeName, storageIndex: number): Range {
  const { segment, nextStart } = alcoveNeighbors(shape, edge, storageIndex);
  const min = segment.position + minAlcoveWidth(segment, edge);
  return { min, max: Math.max(min, nextStart) };
}

/** Smallest length an edge may shrink to, given its chamfers and alcoves. */
function minEdgeLength(shape: FloorPlanRoom, edge: EdgeName): number {
  const endInset = edgeLeg(shape, edge, false);
  return Math.max(edgeLeg(shape, edge, true) + endInset, lastSegmentEnd(shape, edge) + endInset);
}

/** Minimum room width so north/south-edge content still fits. */
export function minWidth(shape: FloorPlanRoom): number {
  return Math.max(minEdgeLength(shape, 'backEdge'), minEdgeLength(shape, 'frontEdge'));
}
/** Minimum room length so east/west-edge content still fits. */
export function minLength(shape: FloorPlanRoom): number {
  return Math.max(minEdgeLength(shape, 'rightEdge'), minEdgeLength(shape, 'leftEdge'));
}

/**
 * Valid range for a line segment's position along its host line (slides
 * between its neighbors; the span excludes primary-edge chamfer legs).
 */
export function lineSegPositionRange(segments: FloorPlanEdgeSegment[], segmentIndex: number, edgeLen: number, minBound = 0, maxBound = edgeLen): Range {
  const segment = segments[segmentIndex];
  let min = minBound, max = maxBound - segment.width;
  for (let index = 0; index < segments.length; index++) {
    if (index === segmentIndex) continue;
    const other = segments[index];
    if (other.position + other.width <= segment.position + 1e-6) min = Math.max(min, other.position + other.width);
    else if (other.position >= segment.position + segment.width - 1e-6) max = Math.min(max, other.position - segment.width);
  }
  return { min: Math.max(minBound, min), max: Math.max(min, Math.min(max, maxBound - segment.width)) };
}

/** Valid range for a line segment's width (grows toward the next neighbor). */
export function lineSegWidthRange(segments: FloorPlanEdgeSegment[], segmentIndex: number, edgeLen: number, maxBound = edgeLen): Range {
  const segment = segments[segmentIndex];
  let maxEnd = maxBound;
  for (let index = 0; index < segments.length; index++) {
    if (index !== segmentIndex && segments[index].position >= segment.position) maxEnd = Math.min(maxEnd, segments[index].position);
  }
  return { min: 0.1, max: Math.max(0.1, maxEnd - segment.position) };
}
